// Standard veterinary anaesthetic drug doses.
// Doses are in mg/kg unless marked otherwise. concentration in mg/mL.
// route: typical route. Sources: AAHA Anesthesia Guidelines, Plumb's Veterinary Drug Handbook.

export const PREMED_DRUGS = [
  {
    id: "acepromazine",
    name: "Acepromazine",
    category: "Phenothiazine sedative",
    species: ["dog", "cat"],
    doseDog: { low: 0.01, high: 0.05, unit: "mg/kg" },
    doseCat: { low: 0.01, high: 0.05, unit: "mg/kg" },
    concentration: 10, // mg/mL
    route: "IV / IM / SC",
    notes: "Avoid in Boxers, Greyhounds, hypotensive patients.",
  },
  {
    id: "methadone",
    name: "Methadone",
    category: "Full mu opioid",
    species: ["dog", "cat"],
    doseDog: { low: 0.2, high: 0.5, unit: "mg/kg" },
    doseCat: { low: 0.1, high: 0.3, unit: "mg/kg" },
    concentration: 10,
    route: "IV / IM",
    notes: "Excellent analgesia for moderate-to-severe pain.",
  },
  {
    id: "buprenorphine",
    name: "Buprenorphine",
    category: "Partial mu opioid",
    species: ["dog", "cat"],
    doseDog: { low: 0.01, high: 0.03, unit: "mg/kg" },
    doseCat: { low: 0.01, high: 0.03, unit: "mg/kg" },
    concentration: 0.3,
    route: "IV / IM / OTM",
    notes: "Mild-to-moderate pain. OTM route well-tolerated in cats.",
  },
  {
    id: "butorphanol",
    name: "Butorphanol",
    category: "Mixed agonist-antagonist opioid",
    species: ["dog", "cat"],
    doseDog: { low: 0.1, high: 0.4, unit: "mg/kg" },
    doseCat: { low: 0.1, high: 0.4, unit: "mg/kg" },
    concentration: 10,
    route: "IV / IM",
    notes: "Short duration; mild analgesia, good sedation.",
  },
  {
    id: "dexmedetomidine",
    name: "Dexmedetomidine",
    category: "Alpha-2 agonist",
    species: ["dog", "cat"],
    doseDog: { low: 2, high: 10, unit: "mcg/kg" },
    doseCat: { low: 5, high: 20, unit: "mcg/kg" },
    concentration: 0.5, // mg/mL -> 500 mcg/mL
    route: "IV / IM",
    notes: "Profound sedation. Causes bradycardia & vasoconstriction.",
    isMicrogram: true,
  },
  {
    id: "midazolam",
    name: "Midazolam",
    category: "Benzodiazepine",
    species: ["dog", "cat"],
    doseDog: { low: 0.1, high: 0.3, unit: "mg/kg" },
    doseCat: { low: 0.1, high: 0.3, unit: "mg/kg" },
    concentration: 5,
    route: "IV / IM",
    notes: "Co-induction agent. Unreliable sedation alone in young/healthy.",
  },
  {
    id: "atropine",
    name: "Atropine",
    category: "Anticholinergic",
    species: ["dog", "cat"],
    doseDog: { low: 0.02, high: 0.04, unit: "mg/kg" },
    doseCat: { low: 0.02, high: 0.04, unit: "mg/kg" },
    concentration: 0.54,
    route: "IV / IM / SC",
    notes: "Treats bradycardia. Avoid routine pre-emptive use.",
  },
  {
    id: "glycopyrrolate",
    name: "Glycopyrrolate",
    category: "Anticholinergic",
    species: ["dog", "cat"],
    doseDog: { low: 0.005, high: 0.01, unit: "mg/kg" },
    doseCat: { low: 0.005, high: 0.01, unit: "mg/kg" },
    concentration: 0.2,
    route: "IV / IM / SC",
    notes: "Longer duration than atropine; does not cross BBB.",
  },
];

export const INDUCTION_DRUGS = [
  {
    id: "propofol",
    name: "Propofol",
    category: "Phenol IV induction",
    species: ["dog", "cat"],
    doseDog: { low: 4, high: 6, unit: "mg/kg" },
    doseCat: { low: 4, high: 8, unit: "mg/kg" },
    concentration: 10,
    route: "IV (slow, to effect)",
    notes: "Titrate to effect. Causes apnoea and hypotension.",
  },
  {
    id: "alfaxalone",
    name: "Alfaxalone",
    category: "Neuroactive steroid",
    species: ["dog", "cat"],
    doseDog: { low: 2, high: 3, unit: "mg/kg" },
    doseCat: { low: 2, high: 5, unit: "mg/kg" },
    concentration: 10,
    route: "IV (slow, to effect)",
    notes: "Smooth induction. Less cardiovascular depression than propofol.",
  },
  {
    id: "ketamine_diazepam",
    name: "Ketamine + Diazepam (1:1)",
    category: "Dissociative co-induction",
    species: ["dog", "cat"],
    doseDog: { low: 5, high: 10, unit: "mg/kg" },
    doseCat: { low: 5, high: 10, unit: "mg/kg" },
    concentration: 50, // ketamine concentration
    route: "IV",
    notes: "Maintains CV stability. Avoid in HCM cats and renal insufficiency.",
  },
  {
    id: "thiopental",
    name: "Thiopental",
    category: "Barbiturate",
    species: ["dog"],
    doseDog: { low: 8, high: 12, unit: "mg/kg" },
    doseCat: { low: 8, high: 12, unit: "mg/kg" },
    concentration: 25,
    route: "IV",
    notes: "Avoid in sighthounds (slow recovery). Perivascular injection irritating.",
  },
  {
    id: "etomidate",
    name: "Etomidate",
    category: "Imidazole",
    species: ["dog", "cat"],
    doseDog: { low: 0.5, high: 2, unit: "mg/kg" },
    doseCat: { low: 0.5, high: 2, unit: "mg/kg" },
    concentration: 2,
    route: "IV",
    notes: "Excellent CV stability — choice for compromised patients.",
  },
];

export const MAINTENANCE_AGENTS = [
  {
    id: "isoflurane",
    name: "Isoflurane",
    category: "Volatile inhalant",
    mac_dog: 1.3,
    mac_cat: 1.6,
    typical_range: "1.2 – 2.5 %",
    notes: "First-choice inhalant. Dose-dependent CV depression.",
  },
  {
    id: "sevoflurane",
    name: "Sevoflurane",
    category: "Volatile inhalant",
    mac_dog: 2.4,
    mac_cat: 2.6,
    typical_range: "2.0 – 3.5 %",
    notes: "Faster induction/recovery than iso. More expensive.",
  },
];

// Volume (mL) to draw up for a SPECIFIC ordered dose.
// dose is expressed in the drug's range unit (mg/kg or mcg/kg).
export function calcVolumeForDose(drug, weightKg, dose) {
  if (!drug || !weightKg || dose == null) return null;
  const factor = drug.isMicrogram ? 0.001 : 1; // mcg -> mg
  const totalMg = dose * weightKg * factor;
  return totalMg / drug.concentration;
}

export function calcDose(drug, weightKg, species) {
  const range = species === "cat" ? drug.doseCat : drug.doseDog;
  if (!range) return null;
  const lowMg = range.low * weightKg;
  const highMg = range.high * weightKg;
  // For drugs in mcg/kg, range values are in mcg
  const factor = drug.isMicrogram ? 0.001 : 1; // convert mcg -> mg for volume calc
  const lowMgEq = lowMg * factor;
  const highMgEq = highMg * factor;
  const lowMl = lowMgEq / drug.concentration;
  const highMl = highMgEq / drug.concentration;
  return {
    lowDose: lowMg,
    highDose: highMg,
    unit: range.unit,
    lowMl,
    highMl,
  };
}
